import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, Checkbox, Spinner, SystemIcon, Typography } from '@volvo/vcdk-react';
import { Suspense, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import FleetAccountRoles from './_components/FleetAccountRoles';
import FleetMemberTableOptions from './_components/FleetMemberTableOptions';
import FleetRoles from './_components/FleetRoles';

import type { RoleHandle } from './_components/RoleHandle';
import type { IFleetMember } from '@/service/vpc/types/IFleetMember';

import EmptyState from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import InformationWithCta from '@/components/InformationWithCta';
import InviteMembersModal from '@/components/modals/InviteMembersModal';
import RemoveMembersModal from '@/components/modals/RemoveMembersModal';
import RoleTypes from '@/components/RoleTypes';
import Table from '@/components/Table';
import Tooltip from '@/components/Tooltip';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';
import {
  getFleetAccountEndUsersQueryOptions,
  getFleetAccountRolesQueryOptions,
  getFleetsQueryOptions,
} from '@/service/vpc/queryOptions';
import { fleet } from '@/utils';

export const Route = createFileRoute('/_authenticated/_account/members/')({
  component: MembersPage,
  errorComponent: ErrorBoundary,
  loader: async (opts) => {
    const fleetAccountId = opts.context.fleetAccountId;
    if (!fleetAccountId) {
      return; // Return early if no fleet account is selected yet
    }

    const fleetAccountMembers = await opts.context.queryClient.ensureQueryData(
      getFleetAccountEndUsersQueryOptions({ fleetAccountId })
    );

    const fleetAccountRoles = await opts.context.queryClient.ensureQueryData(
      getFleetAccountRolesQueryOptions({ fleetAccountId })
    );

    const fleets = await opts.context.queryClient.ensureQueryData(getFleetsQueryOptions({ fleetAccountId }));
    return { fleetAccountMembers, fleetAccountRoles, fleets };
  },
});

function MembersPage() {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const { fleetAccountId, isDigitalAccount, userHasRole } = useFleetAccount();

  const roleRefs = useRef<Map<string, RoleHandle>>(new Map());
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [members, setMembers] = useState<IFleetMember[]>([]);
  const [rowEditIndex, setRowEditIndex] = useState<number | null>(null);

  const { data: fleetAccountMembers, isLoading } = useQuery(
    getFleetAccountEndUsersQueryOptions({ fleetAccountId: fleetAccountId! })
  );
  const { data: fleetAccountRoles } = useQuery(getFleetAccountRolesQueryOptions({ fleetAccountId: fleetAccountId! }));
  const { data: fleets } = useQuery(getFleetsQueryOptions({ fleetAccountId: fleetAccountId! }));

  const saveRow = () => {
    // Call save on all FleetRole components for this row
    roleRefs.current.forEach((ref) => {
      ref.save();
    });
    setRowEditIndex(null);

    // Clear refs after saving
    roleRefs.current.clear();
  };

  const editRow = (rowIndex: number) => {
    // Clear refs when starting to edit a new row
    roleRefs.current.clear();
    // toggle edit mode on the row index
    setRowEditIndex((prev) => (prev === rowIndex ? null : rowIndex));
  };

  const deleteRows = (selectedRows: IFleetMember[]) => {
    setMembers(selectedRows);
    setIsOpen(true);
  };

  const canView = userHasRole(['Owner', 'Manager', 'Viewer', 'Technician'], fleetAccountId!);
  const canEdit = userHasRole(['Owner', 'Manager'], fleetAccountId!);

  const columns = [
    {
      id: 'select',
      size: 50,
      header: ({ table }) => {
        const selectableRows = table.getRowModel().rows.filter((row) => !row.original.roles.includes('Owner'));
        const allSelectableSelected = selectableRows.length > 0 && selectableRows.every((row) => row.getIsSelected());
        const someSelectableSelected = selectableRows.some((row) => row.getIsSelected());

        return (
          <Checkbox
            checked={allSelectableSelected}
            indeterminate={someSelectableSelected && !allSelectableSelected}
            onChange={() => {
              selectableRows.forEach((row) => {
                row.toggleSelected(!allSelectableSelected);
              });
            }}
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          disabled={row.original.roles.includes('Owner')}
          checked={row.getIsSelected()}
          onChange={() => {
            row.toggleSelected(!row.getIsSelected());
          }}
        />
      ),
    },
    {
      accessorFn: (row) => row.firstName + ' ' + row.surname + ' ' + row.email,
      id: 'name',
      size: 300,
      header: t(($) => $.components.table.members_table.name),
      cell: (info) => {
        const member = info.row.original;
        const fullName = `${member.firstName} ${member.surname}`;
        const email = member.email;
        return (
          <div className="flex flex-col items-end md:items-start md:gap-2">
            <Typography
              variant={mq({
                default: 'heading4',
                md: 'body',
              })}
              className="text-subtle"
            >
              {fullName}
            </Typography>
            <Typography
              variant={mq({
                default: 'caption1',
                md: 'body',
              })}
              className="text-subtle"
            >
              {email}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.endUserId,
      id: 'roles',
      header: (
        <>
          {t(($) => $.components.table.members_table.account)}
          <Tooltip
            element={<SystemIcon icon="info" size={20} />}
            content="Roles assigned at the account level apply to all fleets."
            width={180}
          />
        </>
      ),
      cell: (info) => {
        const endUserId = info.getValue<string>();
        const currentRoles = fleetAccountRoles?.filter((r) => r.endCustomerId === endUserId) || [];
        const isEditingThisRow = rowEditIndex !== null && rowEditIndex === info.row.index;
        return (
          <Suspense>
            <FleetAccountRoles
              ref={(instance) => {
                if (instance && isEditingThisRow) {
                  roleRefs.current.set(endUserId, instance);
                }
              }}
              label=""
              isEdit={isEditingThisRow}
              endUserId={endUserId}
              currentRoles={currentRoles}
            />
          </Suspense>
        );
      },
    },
    ...(fleets?.map(
      (f) =>
        ({
          accessorFn: (row: IFleetMember) => row.endUserId,
          id: `fleet_${f.fleetId}`,
          header: fleet.parseFleetName(f.name).fleetName,
          cell: (info) => {
            const endUserId = info.getValue<string>();
            const isEditingThisRow = rowEditIndex !== null && rowEditIndex === info.row.index;
            return (
              <Suspense>
                <FleetRoles
                  ref={(instance) => {
                    if (instance && isEditingThisRow) {
                      roleRefs.current.set(`${f.fleetId}_${endUserId}`, instance);
                    }
                  }}
                  label=""
                  endUserId={endUserId}
                  fleetId={f.fleetId}
                  isEdit={isEditingThisRow}
                />
              </Suspense>
            );
          },
        }) as ColumnDef<IFleetMember>
    ) || []),
    {
      accessorKey: 'actions',
      id: 'actions',
      header: '',
      maxSize: 70,
      cell: (info) => {
        const member = info.row.original;
        const isOwner = member.roles.some((r) => r === 'Owner');
        return (
          <FleetMemberTableOptions
            member={member}
            isEdit={rowEditIndex !== null && rowEditIndex === info.row.index}
            disabled={isOwner || (rowEditIndex !== null && rowEditIndex !== info.row.index)}
            saveRow={() => saveRow()}
            editRow={() => editRow(info.row.index)}
            deleteRows={deleteRows}
          />
        );
      },
    },
  ] as ColumnDef<IFleetMember>[];

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!canView) {
    return (
      <EmptyState
        title="Your access is limited"
        message="You currently have member access. Ask an account administrator to update your role to get access to units, fleets and services."
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {isDigitalAccount ? (
        <InformationWithCta
          icon="info"
          storageKey="Members_InformationWithCta"
          heading={t(($) => $.components.cta.members_digitalaccount_cta.heading)}
          variant="info"
          closable={true}
        >
          <Trans i18nKey={($) => $.components.cta.members_digitalaccount_cta.message} components={{ br: <br /> }} />
        </InformationWithCta>
      ) : (
        <InformationWithCta
          icon="info"
          storageKey="Members_InformationWithCta"
          heading={t(($) => $.components.cta.members_fleetaccount_cta.heading)}
          variant="info"
          closable={true}
        >
          <Typography>
            <Trans i18nKey={($) => $.components.cta.members_fleetaccount_cta.message} components={{ br: <br /> }} />
          </Typography>
        </InformationWithCta>
      )}
      <Table
        key={mqSize}
        isLoading={isLoading}
        data={fleetAccountMembers || []}
        columns={columns}
        headerTitle={t(($) => $.pages.account.members.title)}
        actions={canEdit ? <InviteMembersModal /> : null}
        columnVisibility={{
          select: canEdit,
          actions: canEdit,
        }}
        defaultColumnPinning={{
          left: ['select', 'name'],
        }}
        selectedActions={(selectedRows) => (
          <Button size="medium" variant="secondary" onClick={() => deleteRows(selectedRows)}>
            {t(($) => $.pages.account.members.remove_members_from_account, {
              selectedRows: selectedRows.length,
            })}
          </Button>
        )}
      />
      <RemoveMembersModal members={members} isOpen={isOpen} setIsOpen={setIsOpen} />
      <RoleTypes />
    </div>
  );
}
